import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentUser } from '../services/auth'

/**
 * Landing page after a social OAuth sign-in.
 *
 * The Rails API redirects here with one of:
 *   /auth/callback?token=<JWT>          → success
 *   /auth/callback?error=<description>  → failure
 *
 * On success we store the token, pre-fill the React Query cache with the
 * user profile, then navigate to the booking page (same as email login).
 */
export default function OAuthCallbackPage() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const handled      = useRef(false)   // guard against React StrictMode double-invoke

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    const params   = new URLSearchParams(window.location.search)
    const token    = params.get('token')
    const error    = params.get('error')
    // The `return` param is propagated through OmniAuth's `state` round-trip
    // by callers (BookingModal, SignInPage). It's the only safe way for the
    // post-OAuth redirect to know which surface initiated the sign-in.
    // Default to /portal/dashboard — the new home for authenticated
    // customers — instead of the legacy /book page.
    const returnTo = params.get('return') || '/portal/dashboard'

    if (token) {
      localStorage.setItem('auth_token', token)

      // Pre-populate the user cache so the UI doesn't flash "not logged in"
      getCurrentUser()
        .then((user) => queryClient.setQueryData(['currentUser'], user))
        .catch(() => { /* non-fatal — cache just won't be pre-filled */ })
        .finally(() => navigate(returnTo, { replace: true }))
    } else {
      const msg = error ? decodeURIComponent(error) : 'Sign-in was cancelled or failed.'
      navigate(`/portal/sign-in?error=${encodeURIComponent(msg)}`, { replace: true })
    }
  }, [navigate, queryClient])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
      <svg
        className="animate-spin h-8 w-8 text-brand-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      <p className="text-gray-500 text-sm">Finishing sign-in…</p>
    </div>
  )
}
